import {
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { regionService as sharedRegionService } from "../../../shared/api/regionService";
import { PermissionName } from "../../../shared/types/permission";
import { regionService } from "../shared/api/regionService";
import { UserOptionsResource } from "../shared/types";
import { RegionFormValues, RegionIndexResource, RegionSaveRequest } from "../shared/types/region";
import { RegionCreateDrawer, RegionUpdateDrawer } from "./RegionDrawer";
import { RegionListContextMenu } from "./RegionListContextMenu";

export const RegionList: FC = () => {
	const { t } = useTranslation();

	const crudOperations = useLaravelCrudEntity(
		regionService,
		{
			entityName: t("Region"),
			messages: {
				create: t("Region was saved."),
				update: t("Region was saved."),
				delete: t("Region was deleted."),
			},
		},
		[regionService.indexKey],
		[sharedRegionService.quickFilterKey],
	);

	const dataListColumns: IDrawerDataListColumn<RegionIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<RegionListContextMenu
						regionId={id}
						handleEdit={openDrawer}
						handleDelete={() => crudOperations.deleteEntity(id)}
					/>
				),
			},
		],
		[crudOperations, t],
	);
	const canCreate = useHasPermission<PermissionName>(["region.create"]);
	const canUpdate = useHasPermission<PermissionName>(["region.update"]);

	return (
		<PaginatedDrawerAutoDataList
			canUpdate={canUpdate}
			canCreate={canCreate}
			initialSearch={{ search: "", searchFields: [{ name: "name", label: t("Name") }] }}
			translations={{
				title: t("Regions"),
				empty: t("No regions available"),
			}}
			renderCreateDrawer={(drawerProps) => (
				<RegionCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(mapToRequest(entityFormState))}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<RegionUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, mapToRequest(entityFormState))}
				/>
			)}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(regionService.listKey, regionService.index)}
		/>
	);
};

function mapToRequest(values: RegionFormValues): RegionSaveRequest {
	return {
		...values,
		userIds: values.users.map((user: UserOptionsResource) => user.id) ?? [],
	};
}
